import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  BulkActionModal,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Drawer,
  DropDown,
  Label,
  MLPVersion,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { LoggerService, dateTimeToCustomFormat, getTranslatedMessage } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { useAPICall, useBulkActionState } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import ParticipantService from '../../../sdk/services/participantService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import CampaignService from '../../../sdk/services/campaignService';
import { ParticipantDataStructure } from '../teams/teamsListConfig';
import { chunkArray } from '../../../ui/utils/common';
import {
  MasterData,
  dataSearchOption,
  CampaignOption,
  getParticipantListConfig,
  bulkActionOption,
  bulkActionEditConfig,
} from './participantConfig';

import styles from './index.module.scss';

export const AdminGeneralParticipantList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  const { selectedAction, resetTrigger, clearAll, handleActionInputChange } = useBulkActionState();

  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [displayEmpty, setDisplayEmpty] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ParticipantDataStructure | null>(null);
  const [participantList, setParticipantList] = useState<ParticipantDataStructure[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [defaultSearchOptions, setDefaultSearchOptions] = useState<DynamicObjectInterface>(null);
  const [showBulkActionEditModal, setShowBulkActionEditModal] = useState<Boolean>(false);
  const [selectedData, setSelectedData] = useState<DynamicObjectInterface[]>([]);
  const [checkedItemsCount, setCheckedItemsCount] = useState<number>(0);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchParticipantListByCampaignId();
      // setDefaultSearchOptions([]);
      let searchOptions = sessionStorage.getItem('FilterOptions');
      if (searchOptions) {
        setDefaultSearchOptions(JSON.parse(searchOptions));
      } else {
        setDefaultSearchOptions(null);
      }
    }
  }, [selectedCampaign]);

  const fetchParticipantListByCampaignId = async () => {
    let campaignId = selectedCampaign?.value || '';
    const result = await apiCall(ParticipantService.getAllParticipants, campaignId);
    if (result.status === 200) {
      setDisplayEmpty(result.data.data?.length === 0);

      const _formattedData = result.data.data?.map((item: DynamicObjectInterface, index: number) => {
        item.orderStatus = item.orderStatus === 1 ? 'YES' : 'NO';
        item.isIndividualAchieved =
          item.isIndividualAchieved === 1 ? 'YES' : item.isIndividualAchieved === 0 ? 'NO' : 'PENDING';
        item.participationType = item.participationType === 'ind' ? 'Individual' : 'Team';
        return { ...item, key: ++index };
      });
      setParticipantList(_formattedData ?? []);
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve participant list by campaign');
    }
  };

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve participant list', error);
    }
  };

  const onEdit = (id: ParticipantDataStructure | null) => {
    if (id !== null) {
      navigate(
        `/admin/challenge/manage-team/edit/participant-details?campaignId=${id?.campaignId}&teamId=${id?.teamId}&userId=${id?.userId}`,
      );
    }
  };

  const bulkUpdateParticipant = async (data: DynamicObjectInterface) => {
    const recordList = participantList.filter(list => selectedData.includes(list.key));

    const updatedRecordList = recordList.map(record => ({
      ...record,
      orderStatus: record.orderStatus === 'YES' ? 1 : 0,
      isIndividualAchieved: record.isIndividualAchieved === 'YES' ? 1 : 0,
      ...(data?.status !== undefined && { newStatus: data.status }),
      ...(data?.rewardStatus !== undefined && { newRewardStatus: data.rewardStatus }),
      ...(data?.orderStatus !== undefined && { newOrderStatus: data.orderStatus }),
      ...(data?.isIndividualAchieved !== undefined && { newIsIndividualAchieved: data.isIndividualAchieved }),
    }));

    const chunkSize = 500; // adjust based on your backend’s comfort level
    const chunks = chunkArray(updatedRecordList, chunkSize);

    try {
      for (const chunk of chunks) {
        const result = await apiCall(ParticipantService.bulkUpdateParticipants, { record: chunk });

        if (result.status !== 200 || !result.data.status) {
          throw new Error(
            getTranslatedMessage(t, result.data?.message || 'dashboard.api-message.common.contactAdministrator'),
          );
        }
      }

      await fetchParticipantListByCampaignId();
      adminToast({
        message: getTranslatedMessage(t, 'dashboard.api-message.participant.bulk-update.success'),
        theme: Theme.SUCCESS,
      });
      clearAll();
      setCheckedItemsCount(0);
    } catch (err) {
      adminToast({
        message: (err as Error).message,
        theme: Theme.ERROR,
      });
    }
  };

  const onDeleteAction = async () => {
    try {
      const record = participantList.filter(list => selectedData.includes(list.key));

      const payload = {
        record,
      };

      const response = await apiCall(ParticipantService.bulkDeleteParticipants, payload);

      if (response?.status === 200) {
        if (response.data.status) {
          adminToast({
            message: getTranslatedMessage(t, response.data.message),
            theme: Theme.SUCCESS,
          });
          clearAll();
          setCheckedItemsCount(0);
          fetchParticipantListByCampaignId();
        }
      } else {
        adminToast({
          message: getTranslatedMessage(t, 'dashboard.api-message.common.contactAdministrator'),
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error bulk delete participant', error);
    } finally {
      // Close the modal whether the deletion was successful or not
      setShowDelete(false);
    }
  };

  const onBulkEdit = () => {
    onModalOpenClose();
  };

  const onBulkDelete = () => {
    setShowDelete(!showDelete);
  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    sessionStorage.removeItem('Pagination');
    navigate(`?campaignId=${value.campaignId}`);
  };

  const onModalOpenClose = () => {
    setShowBulkActionEditModal(!showBulkActionEditModal);
  };

  const onConfirmBulkEdit = (data: DynamicObjectInterface) => {
    onModalOpenClose();
    bulkUpdateParticipant(data);
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={t('Are you sure you want to delete this participant? This action cannot be undone!')}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('Confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('Cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const popup = (
    <div data-testid="test-pop-up">
      {showBulkActionEditModal && (
        <BulkActionModal
          labels={{
            modalTitle: 'Bulk Edit',
            confirm: 'Submit',
            cancel: 'Cancel',
          }}
          data={{}}
          onCancel={onModalOpenClose}
          onConfirm={onConfirmBulkEdit}
          onModalClose={onModalOpenClose}
          config={bulkActionEditConfig(t)}
          note={'Note: Changes to “Order Qualified” only applies to those who have NOT done their post-assessment.'}
        />
      )}
    </div>
  );

  const drawerDetails = (
    <>
      {drawerOpen && (
        <Drawer
          onClose={() => setDrawerOpen(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          header={<div>Details</div>}
          drawerWidth={'490px'}
          bottomGapFooter="0px"
          footer={
            <div>
              <Button
                title="Edit"
                onClick={() => onEdit(selectedRow)}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.campaignId')} value={selectedRow?.campaignId} />
                </div>
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('Participant Name')} value={selectedRow?.name} />
                  <Label title={t('ABO ID')} value={selectedRow?.loginId} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('TEAM ID')} value={selectedRow?.teamId.toString()} />
                  <Label title={t('Status')} value={selectedRow?.status} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('Order Status')} value={selectedRow?.orderStatus} />
                  <Label title={t('Achieved')} value={selectedRow?.isIndividualAchieved} />
                </div>
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label
                    title={t('Pre-Assessment Date')}
                    value={
                      selectedRow?.weightInDate ? dateTimeToCustomFormat(selectedRow?.weightInDate, 'DD/MM/YYYY') : '-'
                    }
                  />
                  <Label
                    title={t('Post-Assessment Date')}
                    value={
                      selectedRow?.weightOutDate
                        ? dateTimeToCustomFormat(selectedRow?.weightOutDate, 'DD/MM/YYYY')
                        : '-'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.participant.participantList')} />
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={participantList}
          searchByStrip
          getColumns={getParticipantListConfig}
          data={dataSearchOption}
          defaultSearchOptions={defaultSearchOptions}
          hideAddButton
          rowKey="key"
          selected={`${selectedRow?.key}`}
          cb={{ onEdit, onBulkEdit, onBulkDelete }}
          onRowClick={row => {
            setSelectedRow(row);
            setDrawerOpen(true);
          }}
          enableCheckbox
          showBulkAction
          getBulkActions={bulkActionOption}
          setSelectedKeys={setSelectedData}
          selectedAction={selectedAction}
          resetTrigger={resetTrigger}
          clearAll={clearAll}
          handleActionInputChange={handleActionInputChange}
          checkedItemsCount={checkedItemsCount}
          setCheckedItemsCount={setCheckedItemsCount}
        />
      )}
      {drawerDetails}
      {popup}
      {deletePrompt}
    </div>
  );
};
